<template>
  <div class="heatmap-wrapper">
    <div v-if="hasContent" class="heatmap-card">
      <table class="heatmap-table">
        <thead>
          <tr>
            <th class="sticky-col header-col">Profesional</th>
            <th
              v-for="week in normalizedWeeks"
              :key="week.start"
              class="header-cell"
            >
              <div class="header-cell-content">
                <span class="header-primary">{{ week.label }}</span>
                <span v-if="week.partial" class="header-secondary">(parcial)</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="prof in normalizedProfessionals" :key="prof.id">
            <th class="sticky-col row-label">{{ prof.name }}</th>
            <td
              v-for="week in normalizedWeeks"
              :key="`${prof.id}-${week.start}`"
              class="heatmap-cell"
              :style="cellStyle(prof.id, week.start)"
              :title="cellTooltip(prof.id, week.start, week.label)"
            >
              <div class="cell-content">
                <span class="cell-percentage">{{ formatPercentage(prof.id, week.start) }}</span>
                <span v-if="hasCounts(prof.id, week.start)" class="cell-counts">
                  {{ formatCounts(prof.id, week.start) }}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="legend">
        <span class="legend-label">Tasa de aprobación</span>
        <div class="legend-gradient"></div>
        <div class="legend-range">
          <span>0%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
    <p v-else class="no-data-message">No hay datos de presupuestos aprobados en el período seleccionado.</p>
  </div>
</template>

<script>
export default {
  name: 'ProfesionalApprovalHeatmap',
  props: {
    weeks: {
      type: Array,
      default: () => [],
    },
    professionals: {
      type: Array,
      default: () => [],
    },
    values: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    normalizedWeeks() {
      return Array.isArray(this.weeks)
        ? this.weeks.map((week) => ({
            start: week.start,
            end: week.end,
            label: week.label || this.formatWeekLabel(week.start, week.end),
            partial: Boolean(week.partial),
          }))
        : [];
    },
    normalizedProfessionals() {
      return Array.isArray(this.professionals)
        ? this.professionals.map((prof) => ({
            id: prof.id,
            name: prof.name || 'Profesional sin nombre',
          }))
        : [];
    },
    valueMap() {
      const map = new Map();
      if (!Array.isArray(this.values)) {
        return map;
      }
      this.values.forEach((entry) => {
        if (!entry || typeof entry !== 'object') {
          return;
        }
        const professionalId = entry.professional_id;
        const weekStart = entry.week_start;
        const total = Number.isFinite(entry.total) ? entry.total : 0;
        const approved = Number.isFinite(entry.approved) ? entry.approved : 0;
        const key = `${professionalId}__${weekStart}`;
        map.set(key, {
          total,
          approved,
          rate: total > 0 ? Math.max(0, Math.min(1, approved / total)) : null,
        });
      });
      return map;
    },
    hasContent() {
      return this.normalizedWeeks.length > 0 && this.normalizedProfessionals.length > 0;
    },
  },
  methods: {
    keyFor(professionalId, weekStart) {
      return `${professionalId}__${weekStart}`;
    },
    getStats(professionalId, weekStart) {
      const key = this.keyFor(professionalId, weekStart);
      return this.valueMap.get(key) || { total: 0, approved: 0, rate: null };
    },
    hasCounts(professionalId, weekStart) {
      const { total } = this.getStats(professionalId, weekStart);
      return total > 0;
    },
    formatCounts(professionalId, weekStart) {
      const { approved, total } = this.getStats(professionalId, weekStart);
      if (total === 0) {
        return '';
      }
      return `${approved}/${total}`;
    },
    formatPercentage(professionalId, weekStart) {
      const { rate } = this.getStats(professionalId, weekStart);
      if (rate === null || Number.isNaN(rate)) {
        return '—';
      }
      return `${Math.round(rate * 100)}%`;
    },
    cellTooltip(professionalId, weekStart, weekLabel) {
      const stats = this.getStats(professionalId, weekStart);
      if (stats.total === 0) {
        return `${weekLabel}: sin presupuestos`;
      }
      return `${weekLabel}: ${stats.approved} de ${stats.total} presupuestos aprobados`;
    },
    cellStyle(professionalId, weekStart) {
      const stats = this.getStats(professionalId, weekStart);
      const background = this.rateToColor(stats.rate);
      const color = this.textColor(stats.rate);
      return {
        background,
        color,
      };
    },
    rateToColor(rate) {
      if (rate === null || Number.isNaN(rate)) {
        return '#f0f3f7';
      }
      const clamped = Math.max(0, Math.min(1, rate));
      const red = { r: 244, g: 67, b: 54 };
      const green = { r: 76, g: 175, b: 80 };
      const r = Math.round(red.r + (green.r - red.r) * clamped);
      const g = Math.round(red.g + (green.g - red.g) * clamped);
      const b = Math.round(red.b + (green.b - red.b) * clamped);
      return `rgb(${r}, ${g}, ${b})`;
    },
    textColor(rate) {
      if (rate === null || Number.isNaN(rate)) {
        return '#555';
      }
      return rate > 0.6 ? '#fff' : '#222';
    },
    formatWeekLabel(start, end) {
      if (!start || !end) {
        return '';
      }
      try {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const formatter = new Intl.DateTimeFormat('es-CL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
      } catch (error) {
        return `${start} - ${end}`;
      }
    },
  },
};
</script>

<style scoped>
.heatmap-wrapper {
  width: 100%;
}

.heatmap-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 16px;
  overflow-x: auto;
}

.heatmap-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 4px;
}

.heatmap-table th,
.heatmap-table td {
  padding: 12px 10px;
  text-align: center;
  border-radius: 6px;
  font-size: 0.9em;
}

.header-col {
  min-width: 200px;
  text-align: left;
  background: #f0f3f7;
  font-weight: 600;
  color: #333;
}

.header-cell {
  background: #f0f3f7;
  color: #333;
  font-weight: 600;
  min-width: 140px;
}

.header-cell-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.header-primary {
  font-size: 0.95em;
}

.header-secondary {
  font-size: 0.75em;
  color: #888;
}

.row-label {
  background: #f0f3f7;
  color: #333;
  text-align: left;
  font-weight: 500;
  position: sticky;
  left: 0;
  z-index: 1;
}

.heatmap-cell {
  min-width: 110px;
  height: 70px;
  transition: transform 0.2s ease;
}

.heatmap-cell:hover {
  transform: scale(1.03);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.cell-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.cell-percentage {
  font-size: 1.05em;
  font-weight: 600;
}

.cell-counts {
  font-size: 0.75em;
  opacity: 0.85;
}

.legend {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-label {
  font-size: 0.85em;
  color: #555;
}

.legend-gradient {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f44336 0%, #4caf50 100%);
}

.legend-range {
  display: flex;
  justify-content: space-between;
  font-size: 0.75em;
  color: #666;
}

.no-data-message {
  text-align: center;
  padding: 24px 12px;
  color: #777;
  font-style: italic;
  background: #f8f9fb;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .header-col {
    min-width: 160px;
  }

  .heatmap-cell {
    min-width: 90px;
    height: 60px;
  }
}
</style>
